import styled from "styled-components";
import { Button, Portal, Tooltip } from "@fluentui/react-components";
import {
  Dismiss16Regular,
  Filter16Filled,
  Filter16Regular,
  Pin16Regular,
  ReOrderDotsVertical16Regular,
} from "@fluentui/react-icons";
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import React, { useContext, useRef, useState } from "react";
import { ColumnOptionsContext } from "./ColumnOptions";
import { FilterDropdown } from "./FilterDropdown";
import { Column } from "./types";
import { describeFilter, isColumnFiltered } from "./utils";

type RowProps = {
  column: Column;
  removable: boolean;
  openFilterKey: string | null;
  setOpenFilterKey: (key: string | null) => void;
  dragHandleProps?: Record<string, any>;
  isOverlay?: boolean;
};

const SelectedRow = ({
  column,
  removable,
  openFilterKey,
  setOpenFilterKey,
  dragHandleProps,
  isOverlay,
}: RowProps) => {
  const { onToggleColumnVisibility, strings, taxonomy, t, id } =
    useContext(ColumnOptionsContext);
  const rowRef = useRef<HTMLDivElement>(null);
  const isFiltered = isColumnFiltered(column.filter);
  const summary = isFiltered ? describeFilter(column.filter, { taxonomy, t }) : "";
  const isFilterOpen = openFilterKey === column.key && !isOverlay;

  return (
    <RowInner
      ref={rowRef}
      $active={isFilterOpen || !!isOverlay}
      data-testid={isOverlay ? undefined : `column-options-selected-${column.key}`}
      id={id && !isOverlay ? `${id}-row-${column.key}` : undefined}
    >
      {dragHandleProps ? (
        <Tooltip content={strings.dragHandleTitle} relationship="label">
          <DragHandle
            appearance="subtle"
            size="small"
            icon={<ReOrderDotsVertical16Regular />}
            data-testid={isOverlay ? undefined : `column-options-drag-${column.key}`}
            {...dragHandleProps}
          />
        </Tooltip>
      ) : (
        <HandleSpacer />
      )}
      <LabelWrap>
        <Label title={column.label}>{column.label}</Label>
        {summary && (
          <FilterSummary
            title={summary}
            data-testid={`column-options-filter-summary-${column.key}`}
          >
            {summary}
          </FilterSummary>
        )}
      </LabelWrap>
      {column.filter && (
        <Tooltip
          content={isFiltered ? strings.filterActiveTitle : strings.addFilterTitle}
          relationship="label"
        >
          <IconButton
            appearance="subtle"
            size="small"
            icon={isFiltered ? <Filter16Filled /> : <Filter16Regular />}
            $active={isFiltered}
            onClick={() => setOpenFilterKey(isFilterOpen ? null : column.key)}
            data-testid={`column-options-filter-${column.key}`}
            id={id ? `${id}-filter-icon-${column.key}` : undefined}
          />
        </Tooltip>
      )}
      {removable ? (
        <Tooltip content={strings.removeColumnTitle} relationship="label">
          <IconButton
            appearance="subtle"
            size="small"
            icon={<Dismiss16Regular />}
            $active
            onClick={() => onToggleColumnVisibility(column.key)}
            data-testid={`column-options-remove-${column.key}`}
            id={id ? `${id}-remove-${column.key}` : undefined}
          />
        </Tooltip>
      ) : (
        <Tooltip content={strings.pinnedColumnTitle} relationship="label">
          <PinSlot
            role="img"
            tabIndex={0}
            aria-label={strings.pinnedColumnTitle}
            data-testid={`column-options-pin-${column.key}`}
          >
            <Pin16Regular aria-hidden />
          </PinSlot>
        </Tooltip>
      )}
      {isFilterOpen && (
        <FilterDropdown
          isOpen
          close={() => setOpenFilterKey(null)}
          target={rowRef}
          width={rowRef.current?.getBoundingClientRect().width ?? 0}
          column={column}
        />
      )}
    </RowInner>
  );
};

const SortableRow = (props: Omit<RowProps, "dragHandleProps" | "removable">) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: props.column.key });

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
      }}
    >
      <SelectedRow
        {...props}
        removable
        dragHandleProps={{ ...attributes, ...listeners }}
      />
    </div>
  );
};

export const SelectedColumnsPane = () => {
  const { columns, onReorderColumn, strings, id } =
    useContext(ColumnOptionsContext);
  const [openFilterKey, setOpenFilterKey] = useState<string | null>(null);
  const [activeKey, setActiveKey] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const listed = columns.filter((c) => c.isPresentInColumnOptions !== false);
  const fixedSelected = listed.filter((c) => c.isFixed && c.isVisible);
  const draggableSelected = listed.filter((c) => !c.isFixed && c.isVisible);
  const total = fixedSelected.length + draggableSelected.length;
  const activeColumn = draggableSelected.find((c) => c.key === activeKey);

  const handleDragStart = (event: DragStartEvent) => {
    setOpenFilterKey(null);
    setActiveKey(String(event.active.id));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveKey(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    // Only non-fixed rows are sortable, so nothing can land above the fixed ones.
    const from = columns.findIndex((c) => c.key === active.id);
    const to = columns.findIndex((c) => c.key === over.id);
    if (from !== -1 && to !== -1) onReorderColumn(from, to);
  };

  return (
    <Wrapper
      onKeyDownCapture={(event) => {
        // dnd-kit cancels the drag on Escape; keep the dialog from closing too.
        if (activeKey && event.key === "Escape") event.preventDefault();
      }}
    >
      <Header data-testid="column-options-heading-selected">
        {strings.selectedColumnsLabel} ({total})
      </Header>
      <ScrollArea id={id ? `${id}-columns-selected` : undefined}>
        {total === 0 && <Empty>{strings.noColumnsSelectedLabel}</Empty>}
        {fixedSelected.map((column) => (
          <SelectedRow
            key={column.key}
            column={column}
            removable={false}
            openFilterKey={openFilterKey}
            setOpenFilterKey={setOpenFilterKey}
          />
        ))}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={() => setActiveKey(null)}
        >
          <SortableContext
            items={draggableSelected.map((c) => c.key)}
            strategy={verticalListSortingStrategy}
          >
            {draggableSelected.map((column) => (
              <SortableRow
                key={column.key}
                column={column}
                openFilterKey={openFilterKey}
                setOpenFilterKey={setOpenFilterKey}
              />
            ))}
          </SortableContext>
          {/* The dialog surface is the containing block for fixed elements, which
              would offset the overlay; render it outside the surface instead. */}
          <Portal>
            <DragOverlay>
              {activeColumn ? (
                <OverlayRow>
                  <SelectedRow
                    column={activeColumn}
                    removable
                    openFilterKey={null}
                    setOpenFilterKey={() => {}}
                    dragHandleProps={{}}
                    isOverlay
                  />
                </OverlayRow>
              ) : null}
            </DragOverlay>
          </Portal>
        </DndContext>
      </ScrollArea>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 280px;
  min-height: 0;
  padding-left: 12px;
  border-left: 1px solid ${(props) => props.theme.palette.neutralLight};
`;

const Header = styled.div`
  font-weight: 600;
  padding: 6px 4px 8px 4px;
`;

const ScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
`;

const Empty = styled.div`
  padding: 16px 8px;
  color: ${(props) => props.theme.palette.neutralSecondary};
`;

const RowInner = styled.div<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 2px 4px;
  border-radius: 4px;
  background-color: ${(props) =>
    props.$active ? props.theme.palette.neutralLighter : "transparent"};

  &:hover {
    background-color: ${(props) => props.theme.palette.neutralLighter};
  }
`;

const DragHandle = styled(Button)`
  min-width: 24px;
  cursor: grab;
  color: ${(props) => props.theme.palette.neutralSecondary};
`;

const HandleSpacer = styled.span`
  display: inline-block;
  width: 24px;
  flex-shrink: 0;
`;

const LabelWrap = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`;

const Label = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const FilterSummary = styled.span`
  font-size: 12px;
  color: ${(props) => props.theme.palette.themePrimary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const IconButton = styled(Button)<{ $active?: boolean }>`
  min-width: 24px;
  color: ${(props) =>
    props.$active
      ? props.theme.palette.themePrimary
      : props.theme.palette.neutralSecondary};
`;

const PinSlot = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  color: ${(props) => props.theme.palette.neutralTertiary};
`;

const OverlayRow = styled.div`
  background-color: ${(props) => props.theme.palette.white};
  border: 1px solid ${(props) => props.theme.palette.neutralLight};
  border-radius: 4px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  cursor: grabbing;
`;
